import { Mapping } from "../../../types/primitives";
import { IBackground, IBanner, ITemplateName, ITemplateType } from "../types";

const preheaderPageTypes: Mapping = {
  category: "Catégorie",
  checkout: "Panier",
  funnel: "Tunnel de commande",
  homepage: "Homepage",
  product: "Produit",
};

interface IPreHeaderPriority {
  type: string;
  excludes?: string[];
}

const preheaderPagePriority: IPreHeaderPriority[] = [
  { type: "Toutes les pages" },
  {
    excludes: [preheaderPageTypes.funnel],
    type: "Toutes les pages hors tunnel de commande",
  },
];

interface IOptions {
  // tslint:disable-next-line: no-any
  data: any;
  brandId?: string;
  groupId?: number;
  pageType?: string;
  productId?: string;
  productSku?: number;
  segmentIds?: number[];
  templateName: ITemplateName | null;
  templateType: ITemplateType;
}

/**
 * check groupid or segmentids on banner
 * @param userGroupId
 * @param userSegmentIds
 * @param rawGroupId
 * @param rawSegmentIds
 */
const checkGroupAndSegment = (
  userGroupId?: number,
  userSegmentIds?: number[],
  rawGroupIds?: string,
  rawSegmentIds?: string
): boolean => {
  if (!rawGroupIds && !rawSegmentIds) {
    return true;
  }

  const prismicGroupIds = rawGroupIds
    ? rawGroupIds.split(",").map((x: string): number => parseInt(x, 10))
    : [];
  const prismicSegmentIds = rawSegmentIds
    ? rawSegmentIds.split(",").map((x: string): number => parseInt(x, 10))
    : [];

  let show = !!(
    typeof userGroupId === "number" && prismicGroupIds.includes(userGroupId)
  );
  if (!show && userSegmentIds) {
    const check = (id: number): boolean => prismicSegmentIds.includes(id);
    show = userSegmentIds.some(check);
  }

  return show;
};

/**
 * Check user group and segment ids and
 * filter banners according to allowed group and segments for this banner
 */
const checkBanners = (
  prismicBodies: Mapping[],
  userGroupId?: number,
  userSegmentIds?: number[]
): Mapping[] =>
  prismicBodies.filter((body: Mapping): boolean => {
    const { items = null, primary = {} } = body || {};

    const showBanner = checkGroupAndSegment(
      userGroupId,
      userSegmentIds,
      primary.group_ids,
      primary.segment_ids
    );

    return items && showBanner;
  });

/**
 * dictionary of banner creator
 */
export const bannerCreator = {
  /**
   * homepage banner
   */
  homepage(prismicBodies: Mapping[], { templateName }: IOptions): IBanner[] {
    const currentBlock = templateName === "selections" ? 1 : 0;
    const { items = [] } = prismicBodies[currentBlock] || {};

    return items;
  },

  /**
   * preheader banner
   */
  preheader(
    prismicBodies: Mapping[],
    { pageType, groupId, segmentIds, brandId }: IOptions
  ): IBanner[] {
    const allowedBanners = checkBanners(prismicBodies, groupId, segmentIds);

    if (!allowedBanners || allowedBanners.length < 1) {
      return [];
    }

    /**
     * Define banner to show depending on priority
     */
    const type = preheaderPageTypes[pageType || ""];
    const priority = [...preheaderPagePriority, { type } as IPreHeaderPriority];

    const prismicCatTypes = allowedBanners.map(
      ({ primary: { category_type = null } = {} }: Mapping = {}) =>
        category_type
    ) as Array<string | null>;

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < priority.length; i++) {
      if (!priority[i].type) {
        continue;
      }

      const index = prismicCatTypes.findIndex(
        (catType: string | null) =>
          !!(
            catType &&
            catType === priority[i].type &&
            (!priority[i].excludes ||
              !(priority[i].excludes || []).includes(type))
          )
      );

      if (index >= 0) {
        const { items = [] } = allowedBanners[index] || {};

        return items;
      }
    }

    return [];
  },

  /**
   * underMenu banner
   */
  underMenu(
    prismicBodies: Mapping[],
    { pageType, groupId, segmentIds, brandId }: IOptions
  ): IBanner[] {
    // Check required group and segment ids to show banner
    const allowedBanners = checkBanners(prismicBodies, groupId, segmentIds);

    if (!allowedBanners || allowedBanners.length < 1) {
      return [];
    }

    /**
     * Find banner to banner to depending on page type
     */
    const banners: IBanner[] = [];

    // Return first banner respecting condition
    type IFnBanner = (banner: IBanner) => boolean;

    const findBanner = (condition: IFnBanner): IBanner =>
      allowedBanners
        .map((pageBanners) =>
          pageBanners.items.find((item: IBanner) => condition(item))
        )
        .find((item) => item);

    /**
     * Banner by pageTarget
     */
    const all = findBanner(
      (banner: IBanner): boolean =>
        !!(
          banner.page_type_target &&
          ["all"].includes(banner.page_type_target.toLowerCase()) &&
          banner.brand_id_target === null
        )
    );

    const allWithoutFunnel = findBanner(
      (banner: IBanner): boolean =>
        !!(
          banner.page_type_target &&
          ["all without checkout steps"].includes(
            banner.page_type_target.toLowerCase()
          ) &&
          banner.brand_id_target === null
        )
    );

    const checkout = findBanner(
      (el: IBanner): boolean =>
        !!(
          el.page_type_target &&
          "checkout" === el.page_type_target.toLowerCase() &&
          el.brand_id_target === null
        )
    );

    const brand = findBanner(
      (el: IBanner): boolean =>
        !!(
          el.page_type_target &&
          "brand" === el.page_type_target.toLowerCase() &&
          brandId &&
          typeof el.brand_id_target === "number" &&
          el.brand_id_target + "" === brandId
        )
    );

    /**
     * Banner to display
     */
    if (
      brand &&
      brand.brand_is_permanent &&
      (pageType === "category" || pageType === "product")
    ) {
      banners.push(brand);
    } else if (all) {
      banners.push(all);
    } else if (checkout && pageType === "checkout") {
      banners.push(checkout);
    } else if (allWithoutFunnel) {
      banners.push(allWithoutFunnel);
    } else if (brand && (pageType === "category" || pageType === "product")) {
      banners.push(brand);
    }

    return banners;
  },

  /**
   * Background banner
   */
  background(
    prismicBodies: Mapping[],
    { pageType, templateName, groupId, segmentIds }: IOptions
  ): IBanner[] {
    const backgrounds: IBackground[] = [];
    const bodies = prismicBodies;

    if (!bodies || bodies.length < 1) {
      return [];
    }

    for (const body of bodies) {
      const { primary } = body;

      // find first global banner and set it alone
      const background = primary.banner_image.url || primary.background_color;

      if (background) {
        backgrounds.push(primary);
      }
    }

    return backgrounds;
  },

  /**
   * product banner
   */
  product(
    prismicBodies: Mapping[],
    { groupId, segmentIds, brandId, productSku }: IOptions
  ): IBanner[] {
    type findBannerFun = (o: Mapping) => boolean;

    const findBanner = (searchType: string): findBannerFun => ({
      primary: { page_type_target, attr_id, group_ids, segment_ids },
    }: Mapping): boolean => {
      const hideBanner = !checkGroupAndSegment(
        groupId,
        segmentIds,
        group_ids,
        segment_ids
      );
      const pageType = (page_type_target || "").toLowerCase();
      const attrId = (attr_id || "").toString();

      if (hideBanner || !pageType || searchType !== pageType) {
        return false;
      }

      return (
        searchType === "all" ||
        (searchType !== "all" &&
          attr_id &&
          ((searchType === "brand" && attrId === brandId) ||
            (searchType === "product" && attrId === productSku)))
      );
    };

    const banner =
      prismicBodies.find(findBanner("all")) ||
      prismicBodies.find(findBanner("brand")) ||
      prismicBodies.find(findBanner("product"));

    return banner ? [banner.primary] : [];
  },

  /**
   * category banner
   */
  category(
    prismicBodies: Mapping[],
    { groupId, segmentIds, brandId, templateName }: IOptions
  ): IBanner[] {
    type findBrandBannerFun = (el: Mapping) => boolean;

    const findBrandBanner = (searchType: string): findBrandBannerFun => ({
      primary: { page_type_target, category_id, group_ids, segment_ids },
    }: Mapping): boolean => {
      const hideBanner = !checkGroupAndSegment(
        groupId,
        segmentIds,
        group_ids,
        segment_ids
      );
      const pageType = (page_type_target || "").toLowerCase();
      const categoryId = (category_id || "").toString();

      if (hideBanner || !pageType || searchType !== pageType) {
        return false;
      }

      return (
        searchType === "all" ||
        (searchType === "brand" && categoryId === brandId)
      );
    };

    const index = parseInt(templateName as string, 10);
    const banners =
      prismicBodies.find(findBrandBanner("brand")) ||
      prismicBodies.find(findBrandBanner("all"));

    if (banners && banners.items) {
      return [banners.items[index] as IBanner];
    }

    return [];
  },
};
